import { en } from '../locales/en';
import type { MissionId } from '../types/gcs/mission';
import type { VehicleId } from '../types/gcs/vehicle';

/**
 * Formats a weight value with maximum 2 decimal places and appends 'kg' unit.
 * @param weightKg - The weight value in kilograms
 * @returns Formatted weight string (e.g., "1.25 kg")
 */
export function formatWeightKg(weightKg: number): string {
  const rounded = Math.round(weightKg * 100) / 100;
  return en.units.weightKg.replace('{value}', String(rounded));
}

/**
 * Formats a temperature value in Celsius.
 * @param tempC - The temperature value in Celsius
 * @returns Formatted temperature string (e.g., "25°C")
 */
export function formatTemperatureC(tempC: number): string {
  const rounded = Math.round(tempC * 10) / 10;
  return en.units.temperatureC.replace('{value}', String(rounded));
}

/**
 * Formats a speed value in m/s.
 * @param speedMs - The speed in meters per second
 * @returns Formatted speed string (e.g., "5.2 m/s")
 */
export function formatSpeedMetersPerSec(speedMs: number): string {
  const rounded = Math.round(speedMs);
  return en.units.speedMs.replace('{value}', String(rounded));
}

/**
 * Formats a percentage value.
 * @param pct - The percentage value
 * @returns Formatted percentage string (e.g., "75%")
 */
export function formatPercentage(pct: number): string {
  const rounded = Math.round(pct);
  return en.units.percentage.replace('{value}', String(rounded));
}

/**
 * Formats a distance value in kilometers.
 * @param meters - The distance in meters
 * @returns Formatted distance string (e.g., "2.5 km")
 */
export function formatDistanceMtoKm(meters: number): string {
  const km = meters / 1000;
  const rounded = Math.round(km * 10) / 10;
  return en.units.distanceKm.replace('{value}', String(rounded));
}

/**
 * Formats an altitude value in meters.
 * @param altitudeM - The altitude in meters
 * @returns Formatted altitude string (e.g., "150m")
 */
export function formatAltitudeM(altitudeM: number): string {
  const rounded = Math.round(altitudeM);
  return en.units.altitudeM.replace('{value}', String(rounded));
}

/**
 * Extracts the first 4 characters from a vehicle id, if present.
 * @param vehicleId - The full vehicle id
 * @returns Short vehicle id
 */
export function getShortVehicleId(vehicleId: VehicleId): string {
  return String(vehicleId).slice(0, 4);
}

/**
 * Extracts the first 6 characters from a mission id, if present.
 * @param missionId - The full mission id
 * @returns Short mission id or null when no characters exist
 */
export function getShortMissionId(missionId: MissionId): string {
  return missionId.slice(0, 6);
}

/**
 * Formats latitude and longitude coordinates with fixed precision.
 * @param latitude - Latitude value
 * @param longitude - Longitude value
 * @param precision - Number of decimal places to display
 * @returns Formatted coordinates string (e.g., "32.123456, -97.654321")
 */
export function formatCoordinates(latitude: number, longitude: number, precision = 6): string {
  return `${latitude.toFixed(precision)}, ${longitude.toFixed(precision)}`;
}

/**
 * Formats milliseconds into a countdown time string.
 * @param ms - The time in milliseconds
 * @returns Formatted time string (e.g., "1:23" or "0:45")
 */
export function formatTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
